import React, {useState, useEffect} from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useAxiosPrivate from '../../context/hooks/useAxiosPrivate';
import { Spinner, Button, Table, Form, Modal, Row, Col } from 'react-bootstrap';
import '../../appstyles/ClientsCSS/ClientsDetails.css'
import '../../appstyles/QuotesCSS/OrderDetails.css'
import '../../appstyles/ProductsCSS/ProductsDetail.css'
import DownloadInvoicePDF from '../../components/DownloadInvoicePDF';

const invoiceSchema = Yup.object().shape({
  invoice_number: Yup.string().required('Este campo es obligatorio'),
  invoice_date: Yup.date().typeError('La fecha es obligatoria').required('La fecha es obligatoria'),
  iva: Yup.number().typeError('Debe ser un número').min(0, 'Debe ser positivo').nullable(),
  rete_fuente: Yup.number().typeError('Debe ser un número').min(0, 'Debe ser positivo').nullable(),
  rete_ica: Yup.number().typeError('Debe ser un número').min(0, 'Debe ser positivo').nullable(),
});

const adjustmentSchema = Yup.object().shape({
  adjustment_type: Yup.string().required('Este campo es obligatorio'),
  value: Yup.number()
    .typeError('Debe ser un número')
    .required('Este campo es obligatorio')
    .when('adjustment_type', {
      is: (type) => type === 'Corrección',
      then: (schema) => schema,
      otherwise: (schema) => schema.min(0, 'Debe ser positivo'),
    }),
  reason: Yup.string().required('Este campo es obligatorio'),
});

function AccountPayableClientDetails() {
  const axiosInstance = useAxiosPrivate();
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [financialSettings, setFinancialSettings] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAdjustments, setLoadingAdjustments] = useState(false);

  const {
    register: registerInvoice,
    handleSubmit: handleSubmitInvoice,
    reset: resetInvoice,
    formState: { errors: invoiceErrors }
  } = useForm({
    resolver: yupResolver(invoiceSchema),
  });

  const {
    register: registerAdjustment,
    handleSubmit: handleSubmitAdjustment,
    formState: { errors: adjustmentErrors },
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(adjustmentSchema),
  });

  const watchAdjustmentType = watch('adjustment_type');
  const watchValue = watch('value');
  const isValueInvalid =
    watchAdjustmentType !== 'Anulado' &&
    watchAdjustmentType !== 'Corrección' &&
    (isNaN(Number(watchValue)) || Number(watchValue) <= 0);

  const fetchInvoiceDetails = () => {
    axiosInstance.get(`api/financial/${invoiceId}/`)
      .then(response => {
        const invoiceDetails = response.data;
        setInvoice(invoiceDetails);
        setFinancialSettings(invoiceDetails.adjustments || []);
        resetInvoice({ 
          invoice_number: invoiceDetails.invoice_number,
          invoice_date: invoiceDetails.invoice_date,
          commission: invoiceDetails.commission,
          quote_original_amount: invoiceDetails.quote_original_amount,
          amount_total: invoiceDetails.amount_total,
          amount_paid: invoiceDetails.amount_paid,
          amount_due: invoiceDetails.amount_due,
          iva: invoiceDetails.iva ?? '',
          rete_fuente: invoiceDetails.rete_fuente ?? '',
          rete_ica: invoiceDetails.rete_ica ?? '',
        })
      })
      .catch(error => {
        console.error('Failed to fetch invoice details', error);
        alert('Error al cargar los detalles de la factura. Por favor, inténtelo de nuevo más tarde.');
      });
  };
  
  useEffect(() => {
    fetchInvoiceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, axiosInstance]);

  useEffect(() => {
    if (watchAdjustmentType === 'Anulado') {
      setValue('value', 0);
    }
  }, [watchAdjustmentType, setValue]);

  function capitalizeFirst(text) {
    if (!text) return '';
    const lower = text.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  const handleCloseSettings = () => setShowSettings(false);
  const handleShowSettings = () => setShowSettings(true);

  const formatDate = (date) => {
    const d = new Date(date);
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const handleSaveAdjustment = (data) => {
    setLoading(true)
    axiosInstance.patch(`api/financial/${invoiceId}/`, {
      invoice_number: data.invoice_number,
      invoice_date: formatDate(data.invoice_date),
      iva: data.iva,
      rete_fuente: data.rete_fuente,
      rete_ica: data.rete_ica,
    })
    .then(() => {
    fetchInvoiceDetails();
    setEditing(false);
    setLoading(false)
    })
    .catch(error => {
    console.error('Failed to save invoice details', error);
    });
  };

  const onSubmit = (data) => {
    setLoadingAdjustments(true);
    axiosInstance.post(`api/financial/add-adjustment/`, {
      invoice_id: invoiceId,
      value: data.value,
      adjustment_type: data.adjustment_type,
      reason: data.reason,
    })
    .then(response => {
    setFinancialSettings(prevSettings => [...prevSettings, response.data.adjustment]);
    handleCloseSettings();
    fetchInvoiceDetails();
    setLoadingAdjustments(false);
    })
    .catch(error => {
    console.error('Failed to save financial settings', error);
    });
  };

  const handleCancel = () => {
    setEditing(false);
  };

  if (!invoice) {
    return(
    <div className='loading-container'>
      <p style={{fontFamily: 'Roboto, sans-serif'}}>Cargando...</p>
      <Spinner animation="border" variant="info" style={{width: '4.5rem', height: '4.5rem'}} />
    </div>
    );
  }

  return (
    <div className='maestros-details'>
      <h2>Detalles de cuenta por pagar del cliente</h2>
      <hr />
      <div style={{display: 'flex', flexDirection: 'row'}}>
      <div className='first-col'>
          <p><strong>Consecutivo Orden de Compra:</strong> {invoice.quote_details.consecutive} </p>
          <p><strong>Terminos de Pago (Días):</strong> {invoice.quote_details.payment_terms} </p>
          <p><strong>Cliente:</strong> {capitalizeFirst(invoice.quote_details.client_name)} </p>
      </div>  

      <div className='second-col'>
          <p><strong>Proveedor:</strong> {capitalizeFirst(invoice.quote_details.supplier_name)} </p>
          <p><strong>{invoice.quote_details.products.length === 1 ? 'Producto:' : 'Productos:'}</strong> {invoice.quote_details.products.map(capitalizeFirst).join(', ')}</p>
          <p><strong>Estado:</strong> {invoice.payment_status} </p>
          
      </div>   
      </div>
      <hr/>

      <div className='orders-details-actions' 
        style={{
          marginBottom: '10px'
        }}
      >
      <h4 style={{fontWeight: '700'}}>Ajustes de Factura</h4>
      <div className='orders-actions'>
      {!editing && ( 
        <button 
        className='btn btn-primary' 
        style={{backgroundColor: '#025C5A', borderColor: '#025C5A'}}
        onClick={() => setEditing(true)}
        > 
          Editar
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil-square" viewBox="0 0 16 16" style={{marginLeft: '10px'}}> 
            <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
            <path fill-rule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/>
          </svg>
        </button>
      )}
      </div>
      </div>  
      {editing ? (
      <Form onSubmit={handleSubmitInvoice(handleSaveAdjustment)}>
        <Row>
            {/* Primera Columna */}
            <Col>
                <Form.Group controlId="invoice_number">
                <Form.Label>Número de Factura</Form.Label>
                <Form.Control 
                    type="text" 
                    {...registerInvoice('invoice_number')} 
                    isInvalid={!!invoiceErrors.invoice_number} 
                />
                <Form.Control.Feedback type="invalid">
                    {invoiceErrors.invoice_number?.message}
                </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="invoice_date">
                <Form.Label>Fecha de Factura</Form.Label>
                <Form.Control 
                    type="date" 
                    {...registerInvoice('invoice_date')} 
                    isInvalid={!!invoiceErrors.invoice_date} 
                    disabled={invoice.quote_details.incoterm !== 'Local'}
                />
                <Form.Control.Feedback type="invalid">
                    {invoiceErrors.invoice_date?.message}
                </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="quote_original_amount">
                <Form.Label>Valor Original Cotización</Form.Label>
                <Form.Control 
                    type="text" 
                    value={invoice.quote_original_amount}
                    disabled
                />
                </Form.Group>

                <Form.Group controlId="iva">
                  <Form.Label>IVA (%)</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    {...registerInvoice('iva')}
                    isInvalid={!!invoiceErrors.iva}
                  />
                  <Form.Control.Feedback type="invalid">
                    {invoiceErrors.iva?.message}
                  </Form.Control.Feedback>
                </Form.Group>

            </Col>

            {/* Segunda Columna */}
            <Col>
                <Form.Group controlId="amount_total">
                <Form.Label>Valor Total</Form.Label>
                <Form.Control 
                    type="text" 
                    value={invoice.amount_total}
                    disabled
                />
                </Form.Group> 

                <Form.Group controlId="amount_due">
                <Form.Label>Valor Pendiente</Form.Label>
                <Form.Control 
                    type="text" 
                    value={invoice.amount_due}
                    disabled
                />
                </Form.Group>

                <Form.Group controlId="amount_paid">
                <Form.Label>Valor Pagado</Form.Label>
                <Form.Control 
                    type="text" 
                    value={invoice.amount_paid}
                    disabled
                />
                </Form.Group>

                <Form.Group controlId="rete_fuente">
                  <Form.Label>Retefuente (%)</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    {...registerInvoice('rete_fuente')}
                    isInvalid={!!invoiceErrors.rete_fuente}
                  />
                  <Form.Control.Feedback type="invalid">
                    {invoiceErrors.rete_fuente?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="rete_ica">
                  <Form.Label>Reteica (%)</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    {...registerInvoice('rete_ica')}
                    isInvalid={!!invoiceErrors.rete_ica}
                  />
                  <Form.Control.Feedback type="invalid">
                    {invoiceErrors.rete_ica?.message}
                  </Form.Control.Feedback>
                </Form.Group>

            </Col>
        </Row>
        
        <Button
          variant="secondary"
          type="Submit"
          style={{
            width: 'auto',
            backgroundColor: '#056664',
            border: ' solid 2px #025C5A',
            margin: '0 2px'
          }}
        >
          {loading ? 'Cargando...' : 'Guardar'}
        </Button>
        <Button
          variant="secondary"
          onClick={handleCancel}
          style={{
            width: 'auto',
            backgroundColor: '#056664',
            border: ' solid 2px #025C5A',
            margin: '0 2px'
          }}
        >
          Cancelar
        </Button>
      </Form>    
      ) : (
        <div style={{display: 'flex', flexDirection: 'row'}}>
          <div className='first-col'>
            <p><strong>Número de Factura:</strong> {invoice.invoice_number} </p>
            <p><strong>Moneda:</strong> {invoice.quote_details.currency} </p>
            <p><strong>Fecha de Factura:</strong> {invoice.invoice_date} </p>
            <p><strong>Valor Original Cotización:</strong> {invoice.quote_original_amount} </p>
            <p><strong>IVA (%):</strong> {invoice.iva ?? ''} </p>
          </div>  

          <div className='second-col'>
            <p><strong>Valor Total:</strong> {invoice.amount_total} </p>
            <p><strong>Valor Pendiente:</strong> {invoice.amount_due} </p>
            <p><strong>Valor Pagado:</strong> {invoice.amount_paid} </p>
            <p><strong>Retefuente (%):</strong> {invoice.rete_fuente ?? ''} </p>
            <p><strong>Reteica (%):</strong> {invoice.rete_ica ?? ''} </p>
          </div>   
        </div>
      )}

      <div className='empaque'>
        <Button variant='primary' onClick={handleShowSettings}>
            Añadir Ajuste
        </Button>
        <Table striped bordered hover responsive>
        <thead>
            <tr>
            <th>Valor ({invoice.quote_details.currency})</th>
            <th>Tipo de Ajuste</th>
            <th>Fecha</th>
            <th>Razón</th>
            <th>TRM</th>
            </tr>
        </thead>
        <tbody>
            {financialSettings.map(detail => (
            <tr key={detail.invoice_id}>
                <td>{detail.value}</td>
                <td>{detail.adjustment_type}</td>
                <td>{detail.date}</td>
                <td>{detail.reason}</td>
                <td>{detail.trm}</td>
            </tr>
            ))}
        </tbody>
        </Table>
    </div>


    <div style={{ display: 'flex', alignItems: 'center', marginTop: 20, width: '100%' }}>
      <Button 
        variant="secondary" 
        style={{ width: '20%' }}
        onClick={() => navigate(`/financial/clientspayableinvoices`)}
      >
        Volver
      </Button>
      <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <DownloadInvoicePDF invoiceId={invoiceId} />
      </div>
    </div>

    <Modal show={showSettings} onHide={handleCloseSettings}>
    <Modal.Header closeButton>
        <Modal.Title> <strong>Añadir ajuste para la factura No. {invoice.invoice_id}</strong> </Modal.Title>
    </Modal.Header>
    <Modal.Body>
        <Form onSubmit={handleSubmitAdjustment(onSubmit)}>
            <Form.Group>
            <Form.Label>Tipo de Ajuste</Form.Label>
            <Form.Select 
                id='adjustment_type'
                aria-label="Default select example" {...registerAdjustment('adjustment_type')} isInvalid={!!adjustmentErrors.adjustment_type}>
                <option value="Abono">Abono</option>
                <option value="Nota de crédito">Nota de crédito</option>
                <option value="Nota de débito">Nota de débito</option>
                <option value="Corrección">Corrección</option>
                <option value="Anulado">Anulado</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
                {adjustmentErrors.adjustment_type?.message}
            </Form.Control.Feedback>
            </Form.Group>

            
            {watchAdjustmentType !== 'Anulado' ? (
              <Form.Group>
                <Form.Label>Valor ({invoice.quote_details.currency})</Form.Label>
                <Form.Control
                  type="text"
                  placeholder=""
                  {...registerAdjustment('value')}
                  isInvalid={!!adjustmentErrors.value || isValueInvalid}
                />
                <Form.Control.Feedback type="invalid">
                  {isValueInvalid
                    ? 'El valor debe ser un número mayor a 0'
                    : adjustmentErrors.value?.message}
                </Form.Control.Feedback>
              </Form.Group>
            ) : (
              <Form.Group>
                <Form.Label>Valor ({invoice.quote_details.currency})</Form.Label>
                <Form.Control 
                  type="text" 
                  value={0}
                  disabled
                />
              </Form.Group>
            )}
           <Form.Group>
            <Form.Label>Razón</Form.Label>
            <Form.Control
            as="textarea"
            rows={2}
            placeholder=""
            {...registerAdjustment('reason')}
            isInvalid={!!adjustmentErrors.reason}
            />
            <Form.Control.Feedback type="invalid">
                {adjustmentErrors.reason?.message}
            </Form.Control.Feedback>
            </Form.Group>

        <Button
          variant="primary"
          type="submit"
          style={{backgroundColor: '#025C5A', borderColor: '#025C5A '}} 
          disabled={isValueInvalid && watchAdjustmentType !== 'Anulado'}
        >
          {loadingAdjustments ? 'Guardando...' : 'Guardar'}
        </Button>
        </Form>
    </Modal.Body>
    </Modal>
  
    </div>
  )
}

export default AccountPayableClientDetails